import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';

import { AuthService } from '@auth0/auth0-angular';
import { clearLocalStorageExceptUserAndAuth0Spajs, getItemAsJson, setItemAsJson } from 'app/helpers/storage';
import { ApiModel } from 'shared/models/api.model';
import { AccessRight } from 'shared/models/organization.model';
import { SegmentService } from '../segment.service';
import { OrganizationModel } from './models/organization.model';
import { WindowService } from '../window.service';
import { Auth0Organization, Organization, OrganizationType, User } from './types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const userStorageKey = 'user';
const lastUserOrganizationStorageKey = 'lastUserOrganization';

@Injectable({ providedIn: 'root' })
export class AuthenticatedUserService {
    public user: BehaviorSubject<User>;
    public isLoggedIn: BehaviorSubject<boolean>;
    private isUserImpersonatedByAdmin = new BehaviorSubject<boolean>(false);
    public isUserImpersonatedByAdmin$ = this.isUserImpersonatedByAdmin.asObservable();

    constructor(
        private segmentService: SegmentService,
        private organizationModel: OrganizationModel,
        private apiModel: ApiModel,
        private auth0Service: AuthService,
        private windowService: WindowService
    ) {
        const cookieUser = this.cookieUser;
        const isLoggedIn = _.get(cookieUser, 'accessToken') !== null;
        this.user = new BehaviorSubject(cookieUser);
        this.isLoggedIn = new BehaviorSubject(isLoggedIn);
        combineLatest([this.isLoggedIn, this.user])
            .pipe(
                takeUntilDestroyed(),
                distinctUntilChanged((prev, current) => {
                    const [prevIsLoggedIn, prevUser] = prev;
                    const [currentIsLoggedIn, currentUser] = current;

                    return prevIsLoggedIn === currentIsLoggedIn && prevUser?._id === currentUser?._id;
                })
            )
            .subscribe(([isLoggedIn, user]) => {
                if (isLoggedIn && !_.isEmpty(user)) {
                    this.segmentService.identifyUser(user);
                }

                if (!isLoggedIn) {
                    this.segmentService.resetSegment();
                }
            });

        combineLatest([this.auth0Service.user$, this.user])
            .pipe(
                takeUntilDestroyed(),
                map(([auth0User, user]) => {
                    if (!auth0User || !user) {
                        return false;
                    }

                    return (
                        auth0User.org_type === OrganizationType.Admin &&
                        auth0User.user_email &&
                        user.email &&
                        auth0User.user_email !== user.email
                    );
                })
            )
            .subscribe(this.isUserImpersonatedByAdmin);
    }

    public async updateOrganizationProfile(profile: Pick<Organization, 'name' | 'address'>): Promise<void> {
        const user = this.user.value;
        const updatedOrganization = await this.organizationModel.putProfile(user.organization._id, profile);
        this.cookieUser = { ...user, organization: { ...user.organization, ...updatedOrganization } };
    }

    public authenticate(user: User) {
        this.updateUser(user);
        this.isLoggedIn.next(true);

        this.segmentService.track('User sign-in');
    }

    public logout() {
        this.isLoggedIn.next(false);
        this.cookieLastUserOrganization = _.get(this.cookieUser, 'organization._id');
        this.cookieUser = {} as any;
        this.segmentService.track('User sign-out');

        return this.auth0Service.logout({ logoutParams: { returnTo: document.location.origin } });
    }

    public stopImpersonatingUser() {
        clearLocalStorageExceptUserAndAuth0Spajs();
        this.cookieUser = {} as any;
        this.windowService.nativeWindow.location.reload();
    }

    public updateUser(user: User & { isFirstSignin?: boolean; isInvited?: boolean }) {
        this.cookieUser = _.omit(user, ['isFirstSignin', 'isInvited']);
    }

    public getAllowedBrands(accessRight: AccessRight, retailerId: string) {
        return this.organizationModel
            .getAllowedBrands(accessRight, retailerId)
            .pipe(map((brands) => _.sortBy(brands, ({ name }) => _.toLower(_.deburr(name)))));
    }

    public regenerateApiKey(organizationId: string, keyType: string) {
        return this.apiModel
            .makeHttpObservableRequest({
                method: 'PUT',
                url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/regenerate-api-key`,
                body: { keyType }
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                })
            );
    }

    public setUserOrganizations(organizations: Auth0Organization[]) {
        const user = this.user.value;
        user.assignedOrganizations = organizations;
        this.updateUser(user);
    }

    private set cookieUser(user: User) {
        setItemAsJson(userStorageKey, user);
        if (_.isEmpty(user)) this.user.next(null);
        else this.user.next(user);
    }

    private get cookieUser(): User {
        return getItemAsJson(userStorageKey) as User;
    }

    private set cookieLastUserOrganization(organizationId: string) {
        if (typeof organizationId === 'string' && organizationId.length === 24) {
            setItemAsJson(lastUserOrganizationStorageKey, organizationId);
        }
    }

    private get cookieLastUserOrganization(): string {
        return getItemAsJson(lastUserOrganizationStorageKey);
    }
}
